import React, { useState, useEffect } from 'react';
import { X, FileText, AlertTriangle } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { emailTemplatesAPI, EmailTemplate } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (template: EmailTemplate) => void;
}

const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({ isOpen, onClose, onApply }) => {
  const { sessionToken } = useAuth();
  const { isMobile } = useResponsive();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && sessionToken) {
      loadTemplates();
    }
  }, [isOpen, sessionToken]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTemplate(null);
      setShowConfirm(false);
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    if (!sessionToken) return;
    setIsLoading(true);
    try {
      const response = await emailTemplatesAPI.getTemplates(sessionToken);
      if (response.success) {
        setTemplates(response.templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setShowConfirm(true);
  };

  const handleConfirmApply = () => {
    if (selectedTemplate) {
      onApply(selectedTemplate);
      onClose();
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setSelectedTemplate(null);
  };

  const renderTemplateItem = (template: EmailTemplate) => (
    <button
      key={template.id}
      onClick={() => handleSelect(template)}
      style={{
        width: '100%',
        padding: '16px',
        backgroundColor: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#F3F4F6';
        e.currentTarget.style.borderColor = '#D1D5DB';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#F9FAFB';
        e.currentTarget.style.borderColor = '#E5E7EB';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <FileText size={20} color="#6B7280" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
            {template.name}
          </h4>
          {template.subject && (
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#6B7280' }}>
              {template.subject}
            </p>
          )}
          <p style={{ margin: 0, fontSize: '13px', color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {template.body.substring(0, 80)}...
          </p>
        </div>
      </div>
    </button>
  );

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10003,
        padding: isMobile ? '16px' : '20px'
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: isMobile ? 'calc(100vw - 32px)' : '100%',
          maxWidth: isMobile ? 'calc(100vw - 32px)' : '500px',
          maxHeight: isMobile ? 'calc(100vh - 100px)' : '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>
            {showConfirm ? 'Apply Template?' : 'Choose a Template'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={24} color="#6B7280" />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {showConfirm && selectedTemplate ? (
            /* Confirmation View */
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}
              >
                <AlertTriangle size={24} color="#D97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600', color: '#92400E' }}>
                    This will replace your current content
                  </p>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400E' }}>
                    Your current subject and message will be replaced with the template content.
                  </p>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>
                  {selectedTemplate.name}
                </h3>
                {selectedTemplate.subject && (
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
                    <strong>Subject:</strong> {selectedTemplate.subject}
                  </p>
                )}
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#374151',
                    maxHeight: '200px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {selectedTemplate.body}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleCancelConfirm}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmApply}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f9550d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Apply Template
                </button>
              </div>
            </div>
          ) : isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '16px' }}>No templates available</p>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Create templates in Account Settings → Email Templates
              </p>
            </div>
          ) : (
            /* Template List — grouped by scope if templates have scope info */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(() => {
                const hasScopes = templates.some(t => t.scope && t.scope !== 'personal');
                if (!hasScopes) {
                  // No scope info — render flat list (backward-compatible)
                  return templates.map((template) => renderTemplateItem(template));
                }
                // Group by scope
                const groups: { label: string; templates: EmailTemplate[] }[] = [];
                const globalTemplates = templates.filter(t => t.scope === 'global');
                const orgTemplates = templates.filter(t => t.scope === 'org');
                const personalTemplates = templates.filter(t => !t.scope || t.scope === 'personal');
                if (globalTemplates.length > 0) groups.push({ label: '— Global Templates —', templates: globalTemplates });
                if (orgTemplates.length > 0) {
                  const orgName = orgTemplates[0]?.org_name || 'Organization';
                  groups.push({ label: `— ${orgName} Templates —`, templates: orgTemplates });
                }
                if (personalTemplates.length > 0) groups.push({ label: '— My Templates —', templates: personalTemplates });
                return groups.map((group) => (
                  <React.Fragment key={group.label}>
                    <div style={{ padding: '8px 0 4px', fontSize: '12px', fontWeight: '600', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {group.label}
                    </div>
                    {group.templates.map((template) => renderTemplateItem(template))}
                  </React.Fragment>
                ));
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePickerModal;
